import clsx from 'clsx';
import type { FunctionComponent } from 'react';
import { useMemo } from 'react';

import { BUTTON_SM_ICON_ONLY_SIZE, Button } from '../button/Button';
import {
    DropdownMenu,
    DropdownMenuCheckboxItem,
    DropdownMenuContent,
    DropdownMenuTrigger,
} from '../dropdown-menu/DropdownMenu';
import { IconTableOutlined } from '../icons';
import type { TableColumnWidth } from './Table';

export const CONFIGURE_COLUMNS_TRACK_WIDTH: TableColumnWidth = `calc(${BUTTON_SM_ICON_ONLY_SIZE} + var(--spacing-s))`;

export const getConfigureColumnsTrackWidthWithOffset = (offset: TableColumnWidth): TableColumnWidth =>
    `calc(${CONFIGURE_COLUMNS_TRACK_WIDTH} + ${offset})`;

export type TableConfigurableColumn = {
    readonly id: string;
    readonly label: string;
};

export type TableConfigurableColumnDefinition<TLabelKey extends string = string> = {
    readonly id: string;
    readonly labelKey: TLabelKey;
};

type TableConfigureColumnsMenuProps = {
    readonly columns: readonly TableConfigurableColumn[];
    readonly columnVisibility: Readonly<Record<string, boolean>>;
    readonly onColumnVisibilityChange: (next: Record<string, boolean>) => void;
    readonly triggerAriaLabel: string;
};

export const TableConfigureColumnsMenu: FunctionComponent<TableConfigureColumnsMenuProps> = ({
    columns,
    columnVisibility,
    onColumnVisibilityChange,
    triggerAriaLabel,
}) => (
    <DropdownMenu>
        <DropdownMenuTrigger asChild={true}>
            <Button
                aria-label={triggerAriaLabel}
                className="[&>svg]:text-text-muted"
                iconOnly={true}
                size="sm"
                variant="tertiary"
            >
                <IconTableOutlined size={14} />
            </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
            {columns.map((column) => (
                <DropdownMenuCheckboxItem
                    key={column.id}
                    checked={columnVisibility[column.id] !== false}
                    onCheckedChange={(checked) =>
                        onColumnVisibilityChange({ ...columnVisibility, [column.id]: checked })
                    }
                    onSelect={(event) => event.preventDefault()}
                >
                    {column.label}
                </DropdownMenuCheckboxItem>
            ))}
        </DropdownMenuContent>
    </DropdownMenu>
);

type TableConfigureColumnsCellProps = TableConfigureColumnsMenuProps & {
    readonly className?: string;
};

export const TableConfigureColumnsCell: FunctionComponent<TableConfigureColumnsCellProps> = ({
    className,
    ...menuProps
}) => (
    // Always rendered as the trailing track of a header row, so it is a column header, not a data cell.
    // biome-ignore lint/a11y/useSemanticElements: header cells are CSS-grid tracks, not table markup
    // biome-ignore lint/a11y/useFocusableInteractive: the menu trigger inside owns the focus
    <div className={clsx('flex items-center justify-end py-m pr-s', className)} role="columnheader">
        <TableConfigureColumnsMenu {...menuProps} />
    </div>
);

type TableConfigureColumnsHeaderCellProps<TLabelKey extends string> = {
    readonly className?: string;
    readonly columnVisibility: TableConfigureColumnsMenuProps['columnVisibility'];
    readonly columns: readonly TableConfigurableColumnDefinition<TLabelKey>[];
    readonly onColumnVisibilityChange: TableConfigureColumnsMenuProps['onColumnVisibilityChange'];
    readonly translate: (key: TLabelKey) => string;
    readonly triggerAriaLabel: string;
};

export const TableConfigureColumnsHeaderCell = <TLabelKey extends string>({
    className,
    columnVisibility,
    columns,
    onColumnVisibilityChange,
    translate,
    triggerAriaLabel,
}: TableConfigureColumnsHeaderCellProps<TLabelKey>) => {
    const hideableColumns = useMemo<readonly TableConfigurableColumn[]>(
        () => columns.map(({ id, labelKey }) => ({ id, label: translate(labelKey) })),
        [columns, translate],
    );

    return (
        // No role: unlike `TableConfigureColumnsCell` this renders as the *content* of a column's
        // header, so the surrounding `TableHeaderCell` already supplies `role="columnheader"`.
        <div className={clsx('flex w-full items-center justify-end', className)}>
            <TableConfigureColumnsMenu
                columns={hideableColumns}
                columnVisibility={columnVisibility}
                triggerAriaLabel={triggerAriaLabel}
                onColumnVisibilityChange={onColumnVisibilityChange}
            />
        </div>
    );
};
